<script lang="ts" context="module">
    export enum PageSelectStyle {
        DEFAULT = "default",
        LIGHT = "light",
    }
</script>

<script lang="ts">
    import SVGIcon from "$/components/SVGIcon.svelte";
    import { ClusterStore, ClusterType } from "$/stores/ClusterStore";
    import { TenantStore } from "$/stores/TenantStore";
    import { TokenStore } from "$/stores/TokenStore";
    import { setCurrentSelection } from "$/stores/PageSelectStore";
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
        normalizeTopicName
    } from "$/stores/extension/ActiveTopicStore";
    import { clickAction } from "$/utils/clickAction";
    import { onMount } from "svelte/internal";
    import { fade } from "svelte/transition";
    import Clusters from "./components/Clusters.svelte";
    import { filterClusterTopicsByQuery, getGlobalIndex, getSelectionFromGlobalIndex } from "./utils";
    import { ExtensionPanels, ExtensionStore } from "$/stores/ExtensionStore";
    import type { Cluster } from "./types";
    import InboxTopicItem from "./components/InboxTopicItem.svelte";
    import {cleanupAllHighlights, highlightSnippets, highlightTriggers} from "$/utils/CustomLocateV4/marker/utils";
    import { PreferencesStore } from "$/stores/global/PreferencesStore";
    import { tooltip } from "$/utils/tooltip";
    import { get } from "svelte/store";

    import {
        getEffectivePricingPlan,
        getRole,
        getRootDocument,
    } from "$/utils/Utils";
    import AnimatedBorder from "../animations/AnimatedBorder.svelte";
    import { StoreStatus, StoreStatusState } from "$/models/StoreStatus";
    import LoadingBar from "../LoadingBar/LoadingBar.svelte";

    export let style: PageSelectStyle = PageSelectStyle.DEFAULT;
    export let hideWorkspace = false;

    export let animateOnMount = false;

    // TODO: Extract dialogs into separate store with actions that manipulate it's state. We should have similar solution
    // to current side panel store, where only one dialog can be opened at a time
    let userRole;
    let editOptionInput;
    let searchTopicsInput;
    let showOptionEdit = false;
    let showNotification = null;
    let showOptions = false;
    let showDeleteDialog = false;
    let showLeaveDialog = false;
    let showNewPageTitleEdit = false;
    let showArchiveDialog = false;
    let hiddenClusters = [];
    let currentSelection = { clusterIndex: 0, topicIndex: -1 };
    let topicRefs = {};
    let allClustersTopics = null;
    let updatingTitle= false;

    $: if (searchTopicsInput) {
        currentSelection = { clusterIndex: 0, topicIndex: -1 };
    }

    $: if ($ActiveTopic)
        userRole = getRole(
            $ActiveTopic,
            $TokenStore.user?.id,
            $TenantStore?.tenants,
            $ClusterStore.clusters,
        );
    $: archivedCluster = $ClusterStore.clusters?.find(
        (cluster) => cluster.clusterType === ClusterType.ARCHIVED,
    );
    $: $ClusterStore, archivedCluster && hideArchivedCluster();
    $: effectivePricingPlan = getEffectivePricingPlan($TenantStore?.active);
    $: isMultipleTenants = hideWorkspace
        ? false
        : $TenantStore?.tenants?.length > 1;
    $: if ($ClusterStore.clusters)
        allClustersTopics = ClusterStore.getAllClusterTopics();
    $: filteredClustersWithTopics = filterClusterTopicsByQuery(
        searchTopicsInput,
        $ClusterStore.clusters,
    );
    $: clustersAndActiveTopicLoaded =
        $ClusterStore.clusters !== null && $ActiveTopic !== null;

    function clearSearchInput() {
        searchTopicsInput = "";
    }

    // Define a function to handle the scrolling
    function scrollToCurrentTopic() {
        const currentKey = `${currentSelection.clusterIndex}-${currentSelection.topicIndex}`;
        let selectedItem = topicRefs[currentKey];
        if (selectedItem) {
            selectedItem.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }

    const deleteTopic = async (topic) => {
        ExtensionStore.setLoadingStatus(true);

        ClusterStore.removeTopicFromAllClusters(topic.id);

        if ($ActiveTopic.id === topic.id) {
            await loadFirstNonArchivedTopic();
        }

        highlightSnippets();

        await ClusterStore.deleteTopic(topic.id);

        await TenantStore.refreshCollaboratorsList();

        ExtensionStore.setLoadingStatus(false);
    };

    const leaveTopic = async (topic) => {
        let userTopicRole = $ActiveTopic.roles.find(
            (r) => r.userId === $TokenStore.user?.id,
        );

        ExtensionStore.setLoadingStatus(true);

        await ClusterStore.leaveTopic(topic.id, userTopicRole.id);

        if ($ActiveTopic.id === topic.id) {
            await loadFirstNonArchivedTopic();
        }

        highlightSnippets();

        ExtensionStore.setLoadingStatus(false);
    };

    const archiveTopic = async (topic, archive) => {
        let isActiveTopicArchived = $ActiveTopic.id === topic.id;

        ExtensionStore.setLoadingStatus(true);

        if (archive) await ClusterStore.archiveTopic(topic.id, archive);
        else await ClusterStore.unarchiveTopicToBottomOfList(topic);

        if (
            archive &&
            isActiveTopicArchived &&
            ClusterStore.getAllNonArchivedTopics().length > 0
        ) {
            await loadFirstNonArchivedTopic();
        }
        else {
             await loadInbox();
        }

        highlightSnippets();

        ExtensionStore.setLoadingStatus(false);   
    };

    const loadFirstNonArchivedTopic = async () => {
        if ($PreferencesStore.openInboxOnStartup) {
            loadInbox();
        }

        let topicToLoad = ClusterStore.getFirstNonArchivedTopic();

        if (topicToLoad) {
            await ActiveTopicStore.init(
                topicToLoad.id,
                TokenStore,
                TenantStore,
                ClusterStore
            );
        } else {
            ActiveTopicStore.clear();
        }
    };
    const loadInbox = async () =>{
        await ActiveTopicStore.load(null, TokenStore, TenantStore, ClusterStore);
        return;
    };
    const searchTopicsKeyPressed = (e) => {
        const visibleClusters = getVisibleClusters(filteredClustersWithTopics);
        if (["ArrowDown", "ArrowUp"].includes(e.key)) {
            handleArrowNavigation(e.key, visibleClusters);
            e.preventDefault();

            setTimeout(() => {
                scrollToCurrentTopic();
            }, 0);
        } else if (e.key === "Enter") {
            selectCurrentTopic(); 
        }
    };

    const handleArrowNavigation = (key, visibleClusters) => {
    const getLastClusterDetails = () => {
        const lastClusterIndex = filteredClustersWithTopics.findIndex(
            (cluster) => cluster === visibleClusters[visibleClusters.length - 1]
        );
        const lastTopicIndex = filteredClustersWithTopics[lastClusterIndex].topics.length - 1;
        return { lastClusterIndex, lastTopicIndex };
    };

    const getFirstClusterIndex = () =>
        filteredClustersWithTopics.findIndex(
            (cluster) => cluster === visibleClusters[0]
        );

    const { lastClusterIndex, lastTopicIndex } = getLastClusterDetails();
    const firstClusterIndex = getFirstClusterIndex();

    const isInitialSelection = currentSelection.clusterIndex === 0 && currentSelection.topicIndex === -1;
    const isInboxSelection = currentSelection.clusterIndex === -1 && currentSelection.topicIndex === -1;
    const isLastTopicDown = currentSelection.clusterIndex === lastClusterIndex && currentSelection.topicIndex === lastTopicIndex && key === "ArrowDown";
    const isFirstTopicUp = currentSelection.clusterIndex === firstClusterIndex && currentSelection.topicIndex === 0 && key === "ArrowUp";

    const setLastSelection = () => {
        currentSelection = { clusterIndex: lastClusterIndex, topicIndex: lastTopicIndex };
        setCurrentSelection(lastClusterIndex, lastTopicIndex);
    };
    const setInboxSelection = () => {
        currentSelection = { clusterIndex: -1, topicIndex: -1 }; 
        setCurrentSelection(-1, -1);
    }   

    if (isInitialSelection) {
        if (key === "ArrowDown") {
            setInboxSelection();
        } else if (key === "ArrowUp") {
            setLastSelection();
        }
    } else if (isInboxSelection) {
        if (key === "ArrowDown") {
            currentSelection = { clusterIndex: 0, topicIndex: 0 };
            setCurrentSelection(firstClusterIndex, 0);
        } else if (key === "ArrowUp") {
            setLastSelection();
        }
    } else if (isLastTopicDown || isFirstTopicUp) {
        setInboxSelection();
    } else {

        const totalVisibleTopics = visibleClusters.reduce((acc, cluster) => acc + cluster.topics.length, 0);
        if (totalVisibleTopics === 0) return;

        const totalTopics = filteredClustersWithTopics.reduce((acc, cluster) => acc + cluster.topics.length, 0);

        const visibleClusterIndex = visibleClusters.findIndex(
            (cluster) => cluster === filteredClustersWithTopics[currentSelection.clusterIndex]
        );

        let globalIndexVisible = getGlobalIndex( currentSelection.topicIndex,visibleClusterIndex, visibleClusters);
        let globalIndexAll = getGlobalIndex(currentSelection.topicIndex,currentSelection.clusterIndex,filteredClustersWithTopics);

        if (key === "ArrowDown") {
            do {
                globalIndexVisible = (globalIndexVisible + 1) % totalVisibleTopics;
                globalIndexAll = (globalIndexAll + 1) % totalTopics;
            } while (!isClusterTopicVisible(globalIndexVisible, visibleClusters));
        } else if (key === "ArrowUp") {
            do {
                globalIndexVisible =
                    (globalIndexVisible - 1 + totalVisibleTopics) % totalVisibleTopics;
                globalIndexAll = (globalIndexAll - 1 + totalTopics) % totalTopics;
            } while (!isClusterTopicVisible(globalIndexVisible, visibleClusters));
        }
        updateSelection(globalIndexVisible, globalIndexAll, visibleClusters);
    }
    };


    const updateSelection = (globalIndexVisible, globalIndexAll, visibleClusters) => {
        const newSelectionVisible = getSelectionFromGlobalIndex(globalIndexVisible, visibleClusters);
        const newSelectionAll = getSelectionFromGlobalIndex(globalIndexAll, filteredClustersWithTopics);

        const filteredClusterIndex = filteredClustersWithTopics.findIndex(
            (cluster) => cluster === visibleClusters[newSelectionVisible.clusterIndex]
        );
 
        const isNewClusterVisible = visibleClusters.some(
            (cluster) => cluster === filteredClustersWithTopics[newSelectionAll.clusterIndex]
        );

        const clusterIndexToPass = isNewClusterVisible ? newSelectionAll.clusterIndex : filteredClusterIndex;
        const topicIndexToPass = isNewClusterVisible ? newSelectionAll.topicIndex : newSelectionVisible.topicIndex;

        currentSelection = { clusterIndex: clusterIndexToPass, topicIndex: topicIndexToPass }; 
        setCurrentSelection(clusterIndexToPass, topicIndexToPass);
    };

    const getVisibleClusters = (clusters) => {
            return clusters.filter((cluster) => {
                const isCollapsed = ClusterStore ? ClusterStore.isCollapsed(cluster) : false;
                return !isCollapsed;
            });
        };

     const selectCurrentTopic = () => {
        if (currentSelection.clusterIndex === -1 && currentSelection.topicIndex === -1) {

        let inboxTopic = {
            id: null,
            name: "Inbox",
            archived: false,
            createdOn: 0,
            tenant: {},
            tenantId: $ActiveTopic?.tenantId,
            snippets: null,
            createdBy: {},
            createdById: "",
            publicRole: 0,
            public: false,
            roles: [],
            cluster: null,
            clusterId: null,
        };
            optionSelected(inboxTopic);
        } else {
            const selectedTopic = filteredClustersWithTopics[currentSelection.clusterIndex].topics[currentSelection.topicIndex];
            optionSelected(selectedTopic);      
        }
            clearSearchInput();
    };

    const isClusterTopicVisible = (globalIndex, clusters) => {
        const { clusterIndex } = getSelectionFromGlobalIndex(globalIndex, clusters);
        const cluster = clusters[clusterIndex];
        const collapsed = ClusterStore ? ClusterStore.isCollapsed(cluster) : false;
        return !collapsed;
    };

    // TODO: I'd replace double click with edit icon. Feedback created here: https://toil.kitemaker.co/UD4GRq-Collabwriting/feedback/100
    // Also, all related functionality (open, close, update, reset) should be part of that component

    const selectDoubleClicked = () => {
        if (ActiveTopicStore.isInbox() || allClustersTopics === null) return;

        if (userRole >= TopicAccessLevel.EDITOR) {
            editOptionInput = $ActiveTopic.name;
            showOptions = false;
            showOptionEdit = true;
        } else {
            showNotification = "You do not have permission to edit this topic";
            setTimeout(() => {
                showNotification = null;
            }, 1000);
        }
    };

    const closeEditOptionInput = () => {
        showOptionEdit = false;
    };


    function waitForExtensionReady(): Promise<void> {
        return new Promise((resolve) => {
            const check = () => {
                const store = get(ExtensionStore);
                if (!store.loading) {
                    resolve();
                } else {
                    requestAnimationFrame(check); 
                }
            };
            check();
        });
    }

    const updateOptionTitle = async () => {
        if (!$ActiveTopicStore) return;

        if (updatingTitle) return;
        updatingTitle = true;

        if (editOptionInput.trim() === "") {
            editOptionInput = "Untitled Topic";
        }   
        await waitForExtensionReady();

        let topicUpdateFields = {
            id: $ActiveTopic.id,
            name: editOptionInput,
        };

        ActiveTopicStore.updateTopic(topicUpdateFields);

        ClusterStore.updateTopic(topicUpdateFields);

        showOptionEdit = false;
        showNewPageTitleEdit = false;
        showDeleteDialog = false;
        showArchiveDialog = false;
        updatingTitle = false;

    };

    const editOptionKeyPressed = (e) => {
        let isEnterKeyPressed = e.charCode === 13;

        if (isEnterKeyPressed) {
            updateOptionTitle();
        }
    };

    const optionSelected = async (topic) => {
        showOptions = false;
        showDeleteDialog = false;
        showArchiveDialog = false;

        await loadTopic(topic);
    };

    const loadTopic = async (topic) => {
        if (topic?.id === $ActiveTopic.id) return;

        cleanupAllHighlights();

        ExtensionStore.setLoadingStatus(true);
        
        // Initialize the topic store - this may return immediately if cache is present
        await ActiveTopicStore.init(
            topic.id ?? null,
            TokenStore,
            TenantStore,
            ClusterStore
        );
        
        ExtensionStore.setLoadingStatus(false);

        highlightSnippets();
        
        // if topic loaded from cache rehighlight when update is received
        if ($ActiveTopicStore.state.status === StoreStatus.UPDATING) {
            const unsubscribe = ActiveTopicStore.subscribe(store => {
                if (store.state.status === StoreStatus.UP_TO_DATE) {
                    unsubscribe();
                    highlightSnippets();
                }
            });
        }
    };

    const optionEditClicked = async (topic) => {
        if (topic.id !== $ActiveTopic.id) await optionSelected(topic);

        selectDoubleClicked();
    };

    async function optionLeaveClicked(topic) {
        showOptions = false;
        showLeaveDialog = true;

        if (topic.id !== $ActiveTopic.id) await loadTopic(topic);
    }

    const optionDeleteClicked = async (topic) => {
        // ActiveTopicStore is not loaded yet, most likely the user has just
        // created a new topic and wants to delete it instantly
        if (!topic || !topic.id) return;

        showOptions = false;

        if (userRole === TopicAccessLevel.OWNER) {
            showDeleteDialog = true;
        } else {
            showNotification =
                "You do not have permission to delete this topic";
            setTimeout(() => {
                showNotification = null;
            }, 1000);
        }

        if (topic.id !== $ActiveTopic.id) await loadTopic(topic);
    };

    const optionArchiveClicked = async (topic) => {
        showOptions = false;
        showArchiveDialog = true;

        if (topic.id !== $ActiveTopic.id) await loadTopic(topic);
    };

    const deletionConfirmed = async () => {
        showDeleteDialog = false;

        if (showNewPageTitleEdit) {
            showOptionEdit = false;
            showNewPageTitleEdit = false;
        }

        await deleteTopic({ id: $ActiveTopic.id });
    };

    const archiveConfirmed = async () => {
        showArchiveDialog = false;

        if (showNewPageTitleEdit) {
            showOptionEdit = false;
            showNewPageTitleEdit = false;
        }

        await archiveTopic($ActiveTopic, true);
    };

    function leavingConfirmed() {
        leaveTopic($ActiveTopic);
        showLeaveDialog = false;
    }

    const canAddTopicsToCluster = (cluster: Cluster) => {
        return (
            effectivePricingPlan !== -1 &&
            (cluster?.clusterType === ClusterType.TENANT ||
                cluster?.clusterType === ClusterType.TENANT_DEFAULT)
        );
    };

    const handleAddNewPage = async (clusterId = null) => {
        editOptionInput = normalizeTopicName(document.title);

        showDeleteDialog = false;
        showOptions = false;
        showOptionEdit = true;
        showNewPageTitleEdit = true;
        showArchiveDialog = false;
        cleanupAllHighlights();
        highlightTriggers();

        await ExtensionStore.createTopic($TenantStore?.active.id, clusterId);
    };

    const addNewPageClicked = async () => {
        await handleAddNewPage();
    };

    const addNewClusterPage = async (cluster: Cluster) => {
        // if cluster is hidden, show it
        if (hiddenClusters.includes(cluster)) {
            hiddenClusters = hiddenClusters.filter(item => item !== cluster);
        }
        await handleAddNewPage(cluster.id);
    };

    const hideArchivedCluster = () => {
        if (
            hiddenClusters.find(
                (cluster) => cluster.clusterType === ClusterType.ARCHIVED,
            ) === undefined
        ) {
            hiddenClusters = [...hiddenClusters, archivedCluster];
        }
    };

    const showArchivedCluster = () => {
        if (archivedCluster) {
            const archivedClusterIndex = hiddenClusters.findIndex(
                (cluster) => cluster.clusterType === ClusterType.ARCHIVED,
            );
            if (archivedClusterIndex !== -1) {
                hiddenClusters.splice(archivedClusterIndex, 1);
                hiddenClusters = hiddenClusters;
            }
        }
    };
    
    onMount(() => {
        // On init or rerender expand Archive cluster if the active topic is archived
        if ($ActiveTopic?.archived) {
            showArchivedCluster();
        }

        return () => {
            unsubscribeActiveTopic();
        };
    });

    // on active topic change expand Archive cluster if the topic is archived
    const unsubscribeActiveTopic = ActiveTopic.subscribe((topic) => {
        if (topic?.archived) {
            showArchivedCluster();
        }
    });

    const openSettingsAndScrollToWorkspace = () => {
        ExtensionStore.setPanel(ExtensionPanels.SETTINGS);

        // scroll to the element with id of active tenant id
        setTimeout(() => {
            getRootDocument()
                ?.getElementById($TenantStore?.active.id)
                ?.scrollIntoView({ behavior: "smooth" });
        }, 250);
    };

    const openTopicSelectionMenu = () => {
        if (clustersAndActiveTopicLoaded) showOptions = !showOptions;
    };
</script>

{#if showNotification}
    <div class="notification" transition:fade>
        {showNotification}
    </div>
{/if}

<div class="relative">
    <LoadingBar show={$ActiveTopicStore.state.status !== StoreStatus.UP_TO_DATE} />
</div>

<div
    class="cw-container"
    class:cw-container--light={style === PageSelectStyle.LIGHT}
>
    {#if $ExtensionStore.loading || allClustersTopics?.length > 0 || $ActiveTopic?.snippets.length > 0}
        {#if showOptionEdit === false}
            <AnimatedBorder
                duration={animateOnMount ? 2000 : null}
                borderRadius={"12px 0px 0px 0px"}
            >
                <div
                    class="page-select-wrapper"
                    class:--selected={showOptions}
                    class:page-select-wrapper--light={style ===
                        PageSelectStyle.LIGHT}
                >
                    <div
                        class="page-select-option-text-wrapper"
                        class:page-select-option-text-wrapper--light={style ===
                            PageSelectStyle.LIGHT}
                        on:dblclick={selectDoubleClicked}
                        use:clickAction={openTopicSelectionMenu}
                    >
                        {#if $ClusterStore.clusters === null || $ActiveTopic === null}
                            Loading....
                        {:else if $ActiveTopic}
                            {$ActiveTopic.name}
                        {:else}
                            Not Found
                        {/if}
                    </div>

                    {#if clustersAndActiveTopicLoaded}
                        <div class="select-icon" class:rotate={showOptions}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </div>
                    {/if}
                </div>
            </AnimatedBorder>
        {:else}
            <div class="edit-option-wrapper">
                <!-- svelte-ignore a11y-autofocus -->
                <input
                    class="edit-option-input"
                    class:edit-option-input--light={style ===
                        PageSelectStyle.LIGHT}
                    on:keydown|stopPropagation
                    on:keyup|stopPropagation
                    on:keypress|stopPropagation={editOptionKeyPressed}
                    bind:value={editOptionInput}
                    autofocus
                />
                {#if !$ActiveTopic?.id}
                    <div class="loading-spinner">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="h-3 w-3"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                            />
                        </svg>
                    </div>
                {/if}

                <button
                    class="edit-option-button"
                    class:edit-option-button--light={style ===
                        PageSelectStyle.LIGHT}
                    disabled={!$ActiveTopic?.id}
                    use:tooltip={!$ActiveTopic?.id &&
                        "Loading, please wait"}
                    on:click={updateOptionTitle}
                >
                    <SVGIcon name="check" size={16} />
                </button>
                {#if showNewPageTitleEdit}
                    <button
                        class="delete-option-button"
                        class:delete-option-button--light={style ===
                            PageSelectStyle.LIGHT}
                        disabled={!$ActiveTopic?.id}
                        use:tooltip={!$ActiveTopic?.id &&
                            "Loading, please wait"}
                        on:click={() => optionDeleteClicked($ActiveTopic)}
                    >
                        <SVGIcon name="trash" size={16} />
                    </button>
                {/if}
                {#if !showNewPageTitleEdit}
                    <button
                        class="edit-option-button"
                        class:edit-option-button--light={style ===
                            PageSelectStyle.LIGHT}
                        on:click={closeEditOptionInput}
                    >
                        <SVGIcon name="x" size={16} />
                    </button>
                {/if}
            </div>
        {/if}
    {:else}
        <div class="page-select-empty-state">
            Click + button to create new topic
        </div>
    {/if}

    {#if !showNewPageTitleEdit && effectivePricingPlan !== -1 && (clustersAndActiveTopicLoaded || ($ClusterStore?.clusters?.length > 0 && $ActiveTopic === null))}
        <button
            class="page-select-button"
            class:page-select-button--light={style === PageSelectStyle.LIGHT}
            on:click={addNewPageClicked}
        >
            <SVGIcon name="plus" size={16} />
        </button>
    {/if}

    {#if showOptions}
        <div
            class="page-select-options-wrapper"
            class:page-select-options-wrapper--light={style ===
                PageSelectStyle.LIGHT}
        >
            {#if isMultipleTenants}
                <button
                    class="page-select-workspace-title"
                    on:click={() => openSettingsAndScrollToWorkspace()}
                >
                    <span class="icon">
                        {$TenantStore?.active?.name?.charAt(0)}
                    </span>
                    {$TenantStore?.active?.name}
                </button>
            {/if}
            <div class="search-wrapper">
                <div class="icon-wrapper">
                    <SVGIcon name="magnifying-glass" size={16} height={16} />
                </div>
                <!-- svelte-ignore a11y-autofocus -->
                <input
                    class="search-input"
                    on:keydown={searchTopicsKeyPressed}
                    bind:value={searchTopicsInput}
                    placeholder="Search topics"
                    autofocus
                />
                {#if searchTopicsInput}
                    <button class="button" on:click={clearSearchInput}>
                        <div class="icon-wrapper">
                            <SVGIcon name="close" size={16} height={16} />
                        </div>
                    </button>
                {/if}
            </div>

            <InboxTopicItem {optionSelected} />

            <div
                class="page-select-options"
                class:page-select-options--light={style ===
                    PageSelectStyle.LIGHT}
            >
                <Clusters
                    {archiveTopic}
                    {effectivePricingPlan}
                    {filteredClustersWithTopics}
                    {isMultipleTenants}
                    {topicRefs}
                    {addNewClusterPage}
                    {canAddTopicsToCluster}
                    {optionArchiveClicked}
                    {optionDeleteClicked}
                    {optionEditClicked}
                    {optionLeaveClicked}
                    {optionSelected}
                    on:clearSearchTopicsInput={clearSearchInput}
                />
            </div>
        </div>
    {/if}

    <!-- REFACTOR PROPOSAL: We are repeating ourselves. I would extract this into a separate
    custom dialog modal with proper handler functions and render it here with props for text, actions and labels -->

    {#if showDeleteDialog}
        <div class="dialog">
            <p class="dialog-title">
                Are you sure you want to delete "{$ActiveTopic?.name}"?
            </p>
            <footer class="dialog-footer">
                <button
                    class="dialog-btn"
                    on:click={() => {
                        showDeleteDialog = false;
                    }}
                >
                    Cancel
                </button>
                <button
                    class="dialog-btn dialog-btn--red"
                    on:click={deletionConfirmed}
                >
                    Delete
                </button>
            </footer>
        </div>
    {/if}

    {#if showArchiveDialog}
        <div class="dialog">
            <p class="dialog-title">
                Are you sure you want to archive "{$ActiveTopic?.name}"?
            </p>
            <footer class="dialog-footer">
                <button
                    class="dialog-btn"
                    on:click={() => {
                        showArchiveDialog = false;
                    }}
                >
                    Cancel
                </button>
                <button
                    class="dialog-btn dialog-btn--red"
                    on:click={archiveConfirmed}
                >
                    Archive
                </button>
            </footer>
        </div>
    {/if}

    {#if showLeaveDialog}
        <div class="dialog">
            <p class="dialog-title">
                Are you sure you want to leave "{$ActiveTopic?.name}"?
            </p>
            <footer class="dialog-footer">
                <button
                    class="dialog-btn"
                    on:click={() => {
                        showLeaveDialog = false;
                    }}
                >
                    Cancel
                </button>
                <button
                    class="dialog-btn dialog-btn--red"
                    on:click={leavingConfirmed}
                >
                    Leave
                </button>
            </footer>
        </div>
    {/if}
</div>

<style>
    @import "../../main.css";

    .notification {
        @apply reset absolute z-50 mt-[76px] w-full bg-gray-800 px-4 py-2 text-sm text-white !important;
    }

    .cw-container {
        @apply reset z-20 flex w-full !important;
    }

    .cw-container--light {
        @apply border border-b border-gray-300 bg-gray-200 pr-2 !important;
    }

    .page-select-wrapper {
        @apply reset relative flex flex-1 overflow-hidden bg-gray-800 text-white hover:bg-black shadow-md !important;
    }

    .page-select-wrapper.--selected {
        @apply bg-black shadow-none !important;
    }

    .page-select-wrapper--light {
        @apply flex h-8 items-center rounded-tl-xl bg-gray-200 text-black hover:bg-gray-300 shadow-none !important;
    }

    .page-select-wrapper--light.--selected {
        @apply bg-gray-300 !important;
    }
    .page-select-option-text-wrapper {
        @apply reset m-0 mr-4 min-w-0 flex-1 cursor-pointer select-none appearance-none truncate text-ellipsis bg-transparent px-4 py-2 text-sm font-normal text-white outline-none !important;
    }

    .page-select-option-text-wrapper--light {
        @apply py-1 text-black !important;
    }

    .page-select-options-wrapper {
        @apply reset absolute top-9 z-40 w-full !important;
    }

    .page-select-options-wrapper--light {
        @apply top-8 !important;
    }

    .page-select-options-wrapper::-webkit-scrollbar {
        @apply h-3 w-3 bg-white !important;
    }

    .page-select-options-wrapper::-webkit-scrollbar-thumb {
        @apply border-0 border-l border-solid border-l-white bg-gray-200 hover:bg-gray-300 !important;
    }

    .search-wrapper {
        @apply flex items-center gap-2 border-b border-b-gray-300 bg-gray-200 py-1 pl-4 pr-3.5 text-gray-600 !important;
    }

    .search-wrapper .icon-wrapper {
        @apply grid place-items-center !important;
    }

    .search-input {
        @apply reset w-full text-sm !important;
    }

    .page-select-options {
        @apply top-9 flex max-h-[70vh] flex-col overflow-auto rounded-b-2xl shadow-2xl border-b border-b-gray-300 !important;
    }

    .page-select-options > :last-child {
        @apply rounded-b-2xl !important;
    }

    .page-select-options--light {
        @apply max-h-[300px] !important;
    }

    .page-select-workspace-title {
        @apply reset sticky top-0 z-20 flex w-full items-center justify-start gap-2 bg-gray-400 py-1 pl-4 pr-3.5 text-sm font-normal text-white !important;
    }

    .page-select-workspace-title .icon {
        @apply reset h-4 w-4 rounded-sm bg-gray-500 text-center text-xs text-gray-200 !important;
    }

    .select-icon {
        @apply reset pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-white transition-all !important;
    }

    .select-icon.rotate {
        @apply rotate-180 !important;
    }

    .page-select-wrapper--light .select-icon {
        @apply pointer-events-none px-2 text-black !important;
    }

    .page-select-button {
        @apply reset flex w-10 items-center justify-center bg-gray-800 text-white hover:bg-black !important;
    }

    .page-select-button--light {
        @apply w-8 min-w-8 bg-gray-200 text-black hover:bg-gray-300 !important;
    }

    .page-select-empty-state {
        @apply reset flex flex-1 overflow-hidden bg-gray-800 px-4 py-2 text-sm text-white !important;
    }

    .button {
        @apply reset grid h-5 w-5 shrink-0 cursor-pointer place-items-center rounded text-gray-600 transition-all hover:bg-gray-300 !important;
    }

    .select-icon svg,
    .page-select-button svg,
    .edit-option-button svg,
    .delete-option-button svg,
    .option-button svg {
        @apply reset pointer-events-none h-4 w-4 text-inherit !important;
    }

    .edit-option-wrapper {
        @apply reset flex flex-1 bg-white !important;
    }

    .edit-option-input {
        @apply reset flex-1 bg-white py-2 pl-4 text-sm selection:bg-sky-200 !important;
    }

    .edit-option-input--light {
        @apply py-1 !important;
    }

    .edit-option-button {
        @apply reset flex w-10 items-center justify-center bg-white hover:bg-gray-200 !important;
    }

    .edit-option-button--light {
        @apply w-8 !important;
    }

    .delete-option-button {
        @apply reset flex w-10 items-center justify-center bg-white hover:bg-gray-200 !important;
    }

    .delete-option-button--light {
        @apply w-8 !important;
    }

    .dialog {
        @apply reset absolute top-9 z-20 flex w-full flex-col rounded-b-2xl bg-white shadow-2xl !important;
    }

    .dialog-title {
        @apply reset px-4 py-2 text-sm overflow-ellipsis overflow-hidden !important;
    }

    .dialog-footer {
        @apply flex select-none justify-end bg-transparent px-2 pb-2 pt-1 !important;
    }

    .dialog-btn {
        @apply reset rounded-sm border-none px-2 py-1 text-xs font-normal text-black no-underline hover:bg-gray-200 focus:outline-none !important;
    }

    .dialog-btn--red {
        @apply font-bold text-pink-500 hover:bg-pink-200 !important;
    }

    .loading-spinner {
        @apply m-auto mx-1 bg-white text-gray-500 !important;
    }

    .loading-spinner svg {
        @apply animate-spin;
    }


</style>